import { Suspense } from 'react';

import { AgentSetupProvider } from 'contexts/AgentSetupContext';
import { Navigate, Outlet, Route, useParams } from 'react-router-dom';

import { AgentStream } from 'components/organisms/AgentStream';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { usePendo } from 'hooks/usePendo';
import { generateNextPath } from 'lib/routeHelper';
import { usePersonalAccount } from './hooks/usePersonalAccount';
import AGENT_WIZARD_PATHS from './paths';
import AgentWizardCompany from './Steps/Company';
import AgentWizardScenario from './Steps/Scenario';
import AgentWizardSurvey from './Steps/Survey';
import AgentWizardVoice from './Steps/Voice';

/**
 * Automatically tracks Pendo events for all Agent Wizard pages.
 * Reads accountId and projectId from route params when available.
 */
const PendoRouteTracker = () => {
  const { accountId, projectId } = useParams<{
    accountId?: string;
    projectId?: string;
  }>();
  usePendo({ accountId, projectId });
  return null;
};

const AgentWizardFlowWrapper = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AgentSetupProvider>
        <PendoRouteTracker />
        <Outlet />
      </AgentSetupProvider>
    </Suspense>
  );
};

const AgentWizardRootRedirect = () => {
  const accountId = usePersonalAccount('');

  if (!accountId) {
    return null;
  }

  return (
    <Navigate
      replace
      to={generateNextPath(AGENT_WIZARD_PATHS.COMPANY_ACCOUNT, { accountId })}
    />
  );
};

const AgentWizardCompanyRedirect = () => {
  const isPlgProd = useIsPlgProd();
  const accountId = usePersonalAccount('');

  if (!isPlgProd) {
    return <AgentWizardCompany shouldBePlgProd={false} />;
  }

  if (!accountId) {
    return null;
  }

  return (
    <Navigate
      replace
      to={generateNextPath(AGENT_WIZARD_PATHS.COMPANY_ACCOUNT, { accountId })}
    />
  );
};

// Component wrapper that uses hooks
const AgentWizardRoutes = () => {
  return (
    <Route element={<AgentWizardFlowWrapper />} path="/agent-wizard">
      <Route element={<AgentWizardRootRedirect />} path="" />
      <Route
        element={<AgentWizardCompanyRedirect />}
        path={AGENT_WIZARD_PATHS.COMPANY}
      />
      <Route
        element={<AgentWizardCompany shouldBePlgProd />}
        path={AGENT_WIZARD_PATHS.COMPANY_ACCOUNT}
      />
      <Route element={<AgentWizardSurvey />} path={AGENT_WIZARD_PATHS.SURVEY} />
      <Route element={<AgentWizardVoice />} path={AGENT_WIZARD_PATHS.VOICE} />
      <Route
        element={
          <AgentStream>
            <AgentWizardScenario />
          </AgentStream>
        }
        path={AGENT_WIZARD_PATHS.SCENARIOS}
      />
    </Route>
  );
};

export default AgentWizardRoutes;
